import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { BankReconciliationService } from '../../services/bankReconciliation.service';

interface Props {
  bankAccountId: string;
  institutionId: string;
  onClose: () => void;
}

/** Start a new reconciliation for a bank account (T8, frontend part 1) -- on save, navigates
 * straight to the workspace for the freshly created reconciliation. */
const CreateReconciliationDialog: React.FC<Props> = ({ bankAccountId, institutionId, onClose }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [statementDate, setStatementDate] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const [saving, setSaving] = useState(false);

  const valid = statementDate.trim().length > 0 && openingBalance.trim().length > 0 && closingBalance.trim().length > 0;

  const handleCreate = async () => {
    try {
      setSaving(true);
      const created = await BankReconciliationService.create({
        bankAccountId,
        institutionId,
        statementDate,
        statementOpeningBalance: Number(openingBalance),
        statementClosingBalance: Number(closingBalance),
      });
      toast.showSuccess('Reconciliation created');
      navigate(`/banking/reconciliations/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create reconciliation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.recCreate.page' } as any}>
      <DialogTitle>New Reconciliation</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }} data-help-section="bank.recCreate.section.statement">
        <TextField data-help="bank.recCreate.field.statementDate" label="Statement Date" type="date" InputLabelProps={{ shrink: true }} required
          value={statementDate} onChange={e => setStatementDate(e.target.value)} />
        <TextField data-help="bank.recCreate.field.opening" label="Opening Balance" type="number" required
          value={openingBalance} onChange={e => setOpeningBalance(e.target.value)} />
        <TextField data-help="bank.recCreate.field.closing" label="Closing Balance" type="number" required
          value={closingBalance} onChange={e => setClosingBalance(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.recCreate.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.recCreate.create" variant="contained" disabled={saving || !valid} onClick={handleCreate}>Create</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateReconciliationDialog;
